import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { FormattedMessage } from '../../util/reactIntl';
import { isEmptyReview } from '../../util/data';
import Avatar from '../Avatar/Avatar';
import ResponsiveImage from '../ResponsiveImage/ResponsiveImage';
import ReviewRating from '../ReviewRating/ReviewRating';
import UserDisplayName from '../UserDisplayName/UserDisplayName';
import VerificationBadges from '../VerificationBadges/VerificationBadges';
import css from './ReviewCard.module.css';

/**
 * Extract rental context from review data
 * Note: Transaction/booking data may need to be fetched separately
 * For now, we'll use publicData if available, or provide fallbacks
 */
const getRentalContext = (review) => {
    // Try to get from transaction/booking if available
    const listing = review.listing;

    if (listing) {
        return {
            listingTitle: listing.attributes?.title,
            listingImage: listing.attributes?.publicData?.listingImages,
        };
    }

    return null;
};

/**
 * ReviewCard component displays a single review.
 *
 * Shared between the Store Profile reviews tab and the listing page's own Reviews
 * section. The listing page used to render its own near-identical markup from
 * components/Reviews/Reviews.js, which had visibly drifted from this card (different
 * type scale, outlined instead of filled verification badges, a different rented-item
 * row). One component means the two surfaces cannot drift apart again.
 *
 * Imports the leaf components directly rather than via components/index.js: this now
 * lives in that barrel itself, and a barrel that imports one of its own members creates
 * a circular import.
 */
const ReviewCard = ({
    review,
    intl,
    showRentalContext = true,
    // Real per-reviewer verification data, when the caller has queried it - the listing
    // page has it (ListingPage.duck.js's reviewsByUsers), Store Profile does not. Absent,
    // this keeps the component's original "assume email-verified" default rather than
    // silently dropping the badge there.
    reviewerVerificationData = null,
}) => {
    const author = review.author;
    const date = review.attributes?.createdAt;
    const dateString = date
        ? moment(date).format('MMM DD, YYYY')
        : '';

    const isIdVerified = author?.attributes?.profile?.publicData?.isUserVerified || false;
    const isEmailVerified = reviewerVerificationData
        ? !!(
              reviewerVerificationData.isEmailVerified ||
              reviewerVerificationData.isStripeVerified ||
              reviewerVerificationData.isCompletedTransitions ||
              reviewerVerificationData.isPhoneVerified
          )
        : true;
    const usersEmailVerified = reviewerVerificationData
        ? { user: reviewerVerificationData }
        : { user: { isEmailVerified } };

    const rentalContext = showRentalContext ? getRentalContext(review) : null;

    // A rating-only review is stored with the literal sentinel 'no content'
    // (EMPTY_REVIEW, written by TransactionPage.js) - it is not something to print.
    // components/Reviews/Reviews.js guarded against it and this card did not, so the
    // Store Profile tab has been rendering "no content" as review text; folding the
    // guard in here fixes that alongside making the two surfaces identical.
    const reviewContent = review.attributes?.content;
    const hasReviewContent = !!reviewContent && !isEmptyReview(reviewContent);

    return (
        <div className={css.reviewCard}>
            <div className={css.reviewHeader}>
                <div className={css.reviewerInfo}>
                    <div className={css.avatarCol}>
                        <Avatar className={css.reviewerAvatar} user={author} />
                    </div>
                    <div className={css.contentCol}>
                        <div>
                            <div className={css.topRow}>
                                <span className={css.displayName}>
                                    <UserDisplayName user={author} intl={intl} />
                                </span>
                                <VerificationBadges
                                    isEmailVerified={isEmailVerified}
                                    isIdVerified={isIdVerified}
                                    usersEmailVerified={usersEmailVerified}
                                    className={css.badges}
                                />
                            </div>
                            <div className={css.reviewMeta}>
                                <div className={css.reviewRating}>
                                    <ReviewRating
                                        rating={review.attributes?.rating}
                                        className={css.reviewStars}
                                        reviewStarClassName={css.reviewRatingStar}
                                    />
                                </div>
                                {dateString && (
                                    <div className={css.reviewDate}>{dateString}</div>
                                )}
                            </div>
                        </div>
                        <div className={css.reviewContentWrapper}>
                            {hasReviewContent && (
                                <div className={css.reviewContent}>
                                    {reviewContent}
                                </div>
                            )}

                            {rentalContext && (rentalContext.listingTitle) && (
                                <div className={css.reviewItem}>
                                    <div className={css.itemInfo}>
                                        {rentalContext.listingTitle && (
                                            <div className={css.itemName}>
                                                {rentalContext.listingImage && rentalContext.listingImage.length > 0 && (
                                                    <div className={css.itemImage}>
                                                        <ResponsiveImage
                                                            alt={rentalContext.listingTitle}
                                                            renderCloudinaryImage={{
                                                                secure_url: rentalContext.listingImage[0].secure_url,
                                                                alt: rentalContext.listingTitle,
                                                                variantsToRender: [
                                                                    'square-small',
                                                                ],
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                                <div>
                                                    <FormattedMessage
                                                        id="StoreProfilePage.reviews.rented"
                                                        defaultMessage="Rented:"
                                                    />
                                                    <b>{rentalContext.listingTitle}</b>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

ReviewCard.propTypes = {
    review: PropTypes.object.isRequired,
    intl: PropTypes.object.isRequired,
    showRentalContext: PropTypes.bool,
    reviewerVerificationData: PropTypes.object,
};

export default ReviewCard;
